import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Fingerprint, Trash2, Plus, Loader2, KeyRound, ShieldCheck, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { startRegistration } from "@simplewebauthn/browser";
import { useAuth } from "@/contexts/auth-context";
import { LocalDropboxSettings } from "@/components/local-dropbox-settings";

interface PasskeyItem {
  id: number;
  deviceName: string;
  createdAt: string;
  lastUsedAt: string | null;
}

interface AccountSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AccountSettingsDialog({ open, onOpenChange }: AccountSettingsDialogProps) {
  const { user } = useAuth();
  if (!user) return null;
  return <AccountSettingsContent key={user.id} userId={user.id} open={open} onOpenChange={onOpenChange} />;
}

function AccountSettingsContent({ open, onOpenChange, userId }: AccountSettingsDialogProps & { userId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [newDeviceName, setNewDeviceName] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  const { data: passkeys = [], isLoading, isError, error, refetch } = useQuery<PasskeyItem[]>({
    queryKey: ["/api/passkeys", userId],
    enabled: open,
    queryFn: async ({ signal }) => {
      const response = await apiRequest("GET", "/api/passkeys");
      const items = await response.json() as PasskeyItem[];
      signal.throwIfAborted();
      return items;
    },
    gcTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/passkeys/${id}`),
    onSuccess: () => {
      if (!active.current) return;
      queryClient.invalidateQueries({ queryKey: ["/api/passkeys"] });
      toast({ title: "Passkey removed" });
    },
    onError: () => {
      if (!active.current) return;
      toast({ title: "Failed to remove passkey", variant: "destructive" });
    },
  });

  const handleRegisterPasskey = async () => {
    setIsRegistering(true);
    try {
      const optionsRes = await apiRequest("POST", "/api/passkeys/register-options");
      const options = await optionsRes.json();
      if (!active.current) return;
      const { challengeToken, ...optionsJSON } = options;

      const attResp = await startRegistration({ optionsJSON });
      if (!active.current) return;

      await apiRequest("POST", "/api/passkeys/register-verify", {
        response: attResp,
        deviceName: newDeviceName.trim() || "Passkey",
        challengeToken,
      });

      if (!active.current) return;
      queryClient.invalidateQueries({ queryKey: ["/api/passkeys"] });
      setNewDeviceName("");
      toast({ title: "Passkey registered successfully" });
    } catch (error: any) {
      if (!active.current) return;
      if (error?.name === "NotAllowedError") {
        toast({ title: "Passkey registration was cancelled", variant: "destructive" });
      } else {
        toast({
          title: "Failed to register passkey",
          description: error?.message || "An error occurred",
          variant: "destructive",
        });
      }
    } finally {
      if (active.current) setIsRegistering(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-800 border-gray-600 text-gray-200 w-[calc(100vw-2rem)] max-w-[1100px] sm:max-w-[1100px] h-[min(820px,90dvh)] max-h-[90dvh] flex flex-col overflow-hidden p-0 gap-0">
        <DialogHeader className="shrink-0 px-5 py-5 sm:px-6 border-b border-gray-700">
          <DialogTitle className="text-gray-100 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Account Settings
          </DialogTitle>
          <DialogDescription className="text-gray-400">Manage your personal preferences and sign-in settings.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 flex-col md:flex-row min-h-0 overflow-y-auto md:overflow-hidden">
          <nav aria-label="Account settings categories" className="shrink-0 border-b md:border-b-0 md:border-r border-gray-700 p-3 md:w-56 bg-gray-900/20">
            <div aria-current="page" className="flex items-center gap-2 rounded-md bg-blue-500/15 text-blue-200 px-3 py-2 text-sm font-medium">
              <Settings className="h-4 w-4" />System Settings
            </div>
          </nav>
          <div className="flex-1 min-w-0 p-4 sm:p-6 space-y-6 md:overflow-y-auto">
            <div>
              <h2 className="text-lg font-semibold text-gray-100">System Settings</h2>
              <p className="text-sm text-gray-400 mt-1">Local folder preferences and account sign-in security.</p>
            </div>
            <LocalDropboxSettings userId={userId} open={open} />
          <section aria-labelledby="passkey-settings-heading" className="space-y-3 rounded-lg border border-gray-600 bg-gray-900/25 p-4 sm:p-5">
            <h3 id="passkey-settings-heading" className="flex items-center gap-2 text-sm font-medium text-gray-300">
              <KeyRound className="w-4 h-4" />
              Passkey / Login Settings
            </h3>
            <p className="text-xs text-gray-400">
              Passkeys let you sign in quickly and securely using your device's biometrics (fingerprint, face) or security key — no password needed.
            </p>

            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              </div>
            ) : isError ? (
              <div role="alert" className="text-sm text-red-300">
                {error instanceof Error ? error.message : "Unable to load passkeys."}
                <Button variant="ghost" size="sm" onClick={() => void refetch()}>Retry</Button>
              </div>
            ) : passkeys.length > 0 ? (
              <div className="space-y-2">
                {passkeys.map((pk) => (
                  <div
                    key={pk.id}
                    className="flex items-center justify-between bg-gray-700/50 rounded-md px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Fingerprint className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-200 truncate">{pk.deviceName}</p>
                        <p className="text-xs text-gray-500">
                          Added {new Date(pk.createdAt).toLocaleDateString()}
                          {pk.lastUsedAt && ` · Last used ${new Date(pk.lastUsedAt).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-red-400 hover:bg-gray-600 flex-shrink-0"
                      onClick={() => deleteMutation.mutate(pk.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-3 text-xs text-gray-500">
                No passkeys registered yet
              </div>
            )}

            <div className="flex gap-2 items-center">
              <Input
                placeholder="Device name (optional)"
                value={newDeviceName}
                onChange={(e) => setNewDeviceName(e.target.value)}
                className="bg-gray-700 border-gray-600 text-gray-200 text-sm h-8 placeholder:text-gray-500"
                disabled={isRegistering}
              />
              <Button
                size="sm"
                onClick={handleRegisterPasskey}
                disabled={isRegistering}
                className="flex-shrink-0 h-8"
              >
                {isRegistering ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                <span className="ml-1">Add</span>
              </Button>
            </div>
          </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
